import { z } from 'zod';
import type { BuyGuideToken } from '@sleed/bizcommerce-api';

/**
 * Buy guide (Οδηγός Αγοράς) edit form. Mirrors `BuyGuideToken` and the legacy admin page
 * (Core/client/admin/src/pages/Catalog/BuyGuides/index.js): nm required ≤200, bgc
 * background colour ≤20, iv boolean, si required, html content, img1 image (imgdel1 deletes it).
 * Image bytes upload out-of-band (img1File → binaryRepoImagesApi.uploadBuyGuide, needs a saved id).
 *
 * `catlst` (the categories that show this guide) is NOT written through insert/update: the server
 * owns the link on Category.BuyGuideId and exposes `BuyGuides/MassUpdateCategories` ({buyGuideId,
 * catlst}) for it, so the editor saves the entity first and then syncs the category set when it changed.
 */
const nullableStr = z.string().nullable();

export const buyGuideSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(200, 'validation.tooLong'),
	bgc: z.string().trim().max(20, 'validation.tooLong').nullable(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	html: nullableStr,
	catlst: z.array(z.number()),
	img1: nullableStr,
	imgdel1: z.boolean(),
	img1File: z.instanceof(File).nullable()
});

export type BuyGuideFormValues = z.infer<typeof buyGuideSchema>;

export const EMPTY_BUY_GUIDE: BuyGuideFormValues = {
	nm: '',
	bgc: null,
	iv: true,
	si: 0,
	html: null,
	catlst: [],
	img1: null,
	imgdel1: false,
	img1File: null
};

export function tokenToForm(token: BuyGuideToken, categoryIds?: number[] | null): BuyGuideFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		bgc: token.bgc ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		html: token.html ?? null,
		// GetById does not project the category set (only GetAll does) — the caller passes it in.
		catlst: [...(categoryIds ?? token.catlst ?? [])],
		img1: token.img1 ?? null,
		imgdel1: false,
		img1File: null
	};
}

export function formToToken(form: BuyGuideFormValues): BuyGuideToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		bgc: form.bgc?.trim() || null,
		iv: form.iv,
		si: form.si,
		html: form.html,
		img1: form.img1,
		imgdel1: form.imgdel1
	};
}

/** True when the category assignment differs from the saved one (order-insensitive). */
export function categoriesChanged(saved: readonly number[], next: readonly number[]): boolean {
	if (saved.length !== next.length) return true;
	const a = new Set(saved);
	return next.some(id => !a.has(id));
}
